import React from 'react';
import { ArrowUpRight, ArrowDownRight, ChevronRight, Minus } from 'lucide-react';

interface Props {
  label: string;
  value: string | number;
  changePct?: number;
  icon?: React.ComponentType<{ className?: string }>;
  hint?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

const StatCard: React.FC<Props> = ({ label, value, changePct, icon: Icon, hint, onClick, ariaLabel }) => {
  const hasChange = typeof changePct === 'number' && isFinite(changePct);
  const up = (changePct || 0) >= 0;

  const content = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-gray-400" />}
      </div>
      <div className="mt-2 flex items-end justify-between">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        {hasChange &&
          (changePct !== 0 ? (
            <span
              className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                up ? 'text-green-600' : 'text-red-600'
              }`}
              title="vs. previous period"
            >
              {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
              {Math.abs(changePct).toFixed(1)}%
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5 text-xs text-gray-400" title="vs. previous period">
              <Minus className="h-3.5 w-3.5" />
              0%
            </span>
          ))}
      </div>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
      {onClick && (
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600">
          View breakdown
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel || `View ${label} breakdown`}
        className="w-full bg-white border rounded-lg p-5 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50/30 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        {content}
      </button>
    );
  }

  return <div className="bg-white border rounded-lg p-5">{content}</div>;
};

export default StatCard;
